import "@fontsource-variable/inter"
import "@fontsource-variable/plus-jakarta-sans"

import { comoSeLlama, type Empresa } from "@/componentes/empresa/tipos"

/**
 * Los datos del manual de identidad documental (30/10). **El único sitio que los dice.**
 * La pantalla `/identidad` los enseña y los rótulos los usan.
 *
 * ⚠ **El manual manda sobre el lenguaje anterior de los rótulos**: fuera el panel vino
 * macizo, la banda del pie y el filete ondulado de la firma de correo. Papel en más del
 * 85 % de la pieza, ondas translúcidas y oro sólo en filetes: es lo que pidió la minuta
 * para no gastar tinta.
 *
 * ⚠ **La fuente va empaquetada**, no por Google Fonts: el cuerpo de cada línea se calcula
 * con su ancho, y una fuente de respaldo partiría en la impresora lo que cabía en pantalla.
 */

export type Compania = "SETIECA_AGENTES" | "SETIECA_TRADE"

export const COMPANIAS: Record<
  Compania,
  { selector: string; razon: string; descriptor: string; lugar: string | null }
> = {
  SETIECA_AGENTES: {
    selector: "Setieca Agentes Aduanales",
    razon: "Setieca Agentes Aduanales, C.A.",
    descriptor: "Agentes Aduanales",
    lugar: "Puerto Cabello, Venezuela",
  },
  // ⚠ Su logo vectorial no ha llegado: `MarcaCompania` pinta el nombre en texto y
  // Rotulación lo avisa. No se dibuja una marca que no es la suya.
  SETIECA_TRADE: {
    selector: "Setieca Trade Logistics Corp",
    razon: "Setieca Trade Logistics Corp",
    descriptor: "Trade Logistics Corp",
    lugar: null,
  },
}

/** La línea del pie. La razón social de la agencia sale de «Empresa», no de aquí. */
export function pieDeCompania(compania: Compania, empresa?: Empresa): string {
  const { razon, lugar } = COMPANIAS[compania]
  const nombre = compania === "SETIECA_AGENTES" && empresa ? comoSeLlama(empresa) || razon : razon
  return lugar ? `${nombre} · ${lugar}` : nombre
}

/** La tabla «04 · Codificación de archivo» del manual. Sólo trae estos cuatro. */
export const CODIGOS_ARCHIVO = [
  { codigo: "AD", departamento: "Administración" },
  { codigo: "FI", departamento: "Finanzas" },
  { codigo: "OA", departamento: "Operaciones Aduaneras" },
  { codigo: "TH", departamento: "Talento Humano" },
] as const

/** «Finanzas» → «FI–». Vacío si el departamento no está en la tabla. */
export function prefijoDeCodigo(departamento: string): string {
  const buscado = departamento.trim().toLowerCase()
  const fila = CODIGOS_ARCHIVO.find((c) => c.departamento.toLowerCase() === buscado)
  return fila ? `${fila.codigo}–` : ""
}

/** Las cartulinas de la vista previa. El color es del papel: NO se imprime. */
export const PAPELES = [
  { etiqueta: "Blanco", color: "var(--impreso-papel)" },
  { etiqueta: "Amarillo", color: "var(--impreso-cartulina-amarilla)" },
  { etiqueta: "Celeste", color: "var(--impreso-cartulina-celeste)" },
  { etiqueta: "Rosado", color: "var(--impreso-cartulina-rosada)" },
] as const
